import { Stack, useTheme } from '@mui/joy';
import { mapValues } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionButton, AgGridServerTable } from 'core/components';
import { LabeledValue } from 'core/elements';
import { ActionButtonType } from 'core/enums';
import { AgGridServerTableProvider, useStepsContext } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { useAddBomPartsToQuoteContext } from 'quotes/components/AddBomPartsToQuote';
import { getColumnDef } from 'quotes/components/CustomerQuoteList';
import { QuoteEntityType, QuoteListType, QuoteState, QuoteStatus } from 'quotes/enums';
import { quoteService } from 'quotes/services';
import { IBaseQuote } from 'quotes/types';
import { Color, Direction, Level, Variant } from 'theme/enums';

import { getInitialState } from './utils';

interface IProps {
  sourceQuote?: IBaseQuote;
  entityType?: QuoteEntityType;
}

export const SelectCustomerQuote = ({
  sourceQuote,
  entityType = QuoteEntityType.QUOTE,
}: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { goToNextStep } = useStepsContext();
  const { selectedContact, setSelectedQuote } = useAddBomPartsToQuoteContext();

  const columnDefs = useMemo(
    () => getColumnDef(theme, entityType, { quoteListType: QuoteListType.COPY_PARTS_TO_QUOTE }),
    [entityType, theme]
  );

  const initialState = useMemo(() => getInitialState(entityType), [entityType]);

  const tableId = useMemo(() => `customer${entityType}ListTable`, [entityType]);

  const tableEntityType = useMemo(() => `Customer${entityType}`, [entityType]);

  const onSelectionChanged = useCallback(
    ({ api }) => {
      const gridSelectedRows = api?.getSelectedNodes();

      setSelectedQuote(gridSelectedRows[0]?.data);
    },
    [setSelectedQuote]
  );

  const mapFilters = useCallback((filters) => {
    let mappedFilters = filters;

    mappedFilters = mapValues(mappedFilters, (value, key) => {
      switch (key) {
        case 'archived':
          return 1 === value.length ? (value[0] === QuoteState.ACTIVE ? 0 : 1) : null;
        case 'status':
          return value.join('|');
        case 'closeConfidence':
          return `"${value}"`;
        default:
          return value;
      }
    });

    return mappedFilters;
  }, []);

  const getRows = useCallback(
    async ({ filters, ...rest }: AgGridGetRowsParams) => {
      const mappedFilters = mapFilters(filters);

      let statusFilter = mappedFilters?.status || '';

      if (QuoteEntityType.ORDER === entityType && !statusFilter) {
        statusFilter = [
          QuoteStatus.ORDER_DRAFT_VENDOR,
          QuoteStatus.ORDER_REQUESTED_BY_CUSTOMER,
        ].join('|');
      }

      return await quoteService.getCustomerQuotes({
        filters: {
          ...mappedFilters,
          status: statusFilter,
          isOrder: QuoteEntityType.ORDER === entityType ? 1 : 0,
          customerContactName: selectedContact.name,
          ...(sourceQuote?.id ? { excludeIds: [sourceQuote.id] } : {}),
        },
        ...rest,
      });
    },
    [entityType, mapFilters, selectedContact.name, sourceQuote?.id]
  );

  const actionButtons = useMemo(
    () => [
      <ActionButton
        key="addNewQuote"
        action={{
          label: t('buttonLabelCreateNewCustomerQuote', { entityType }),
          type: ActionButtonType.BUTTON,
          color: Color.SUCCESS,
          variant: Variant.SOLID,
          onClick: () => {
            setSelectedQuote(null);
            goToNextStep();
          },
        }}
      />,
    ],
    [entityType, goToNextStep, setSelectedQuote, t]
  );

  return (
    <>
      <AgGridServerTableProvider
        tableId={tableId}
        entityType={tableEntityType}
        columnDefs={columnDefs}
        initialState={initialState}
        getRows={getRows}
        onSelectionChanged={onSelectionChanged}
        selectionMode="singleRow"
        enableClickSelection
      >
        <Stack spacing={1} sx={{ display: 'flex', height: '100%' }}>
          <LabeledValue
            label={t('labelCustomerQuoteCustomer')}
            value={selectedContact?.name}
            options={{
              labelLevel: Level.BODY_SM,
              valueLevel: Level.TITLE_MD,
              wrapperProps: {
                spacing: 0.5,
                direction: Direction.ROW,
                alignItems: 'center',
              },
            }}
          />

          <AgGridServerTable actionButtons={actionButtons} />
        </Stack>
      </AgGridServerTableProvider>
    </>
  );
};
